import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useToast } from "../../contexts/ToastContext";
import { api } from "../../utils/api";
import duLogo from "../../assets/du-logo.svg";
import { useAuth } from "../../contexts/AuthContext";
import Modal from "../../components/ui/modal";

export default function Auth() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login: setAuth, logout } = useAuth();
  const { showToast } = useToast();
  const initialMode = params.get("mode") || "login";
  const [mode, setMode] = useState(initialMode);
  const isLogin = mode === "login";
  const [loading, setLoading] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    // phone: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /* ========================= VALIDATION ========================= */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      email: "",
      // phone: "",
      password: "",
      confirmPassword: "",
    };

    let isValid = true;

    // Login validations
    if (isLogin) {
      if (!form.email.trim()) {
        newErrors.email = "Email is required";
        isValid = false;
      } else if (!validateEmail(form.email)) {
        newErrors.email = "Please enter a valid email address";
        isValid = false;
      }

      if (!form.password) {
        newErrors.password = "Password is required";
        isValid = false;
      }
    } 
    // Register validations
    else {
      if (!form.name.trim()) {
        newErrors.name = "Full name is required";
        isValid = false;
      }

      if (!form.email.trim()) {
        newErrors.email = "Email is required";
        isValid = false;
      } else if (!validateEmail(form.email)) {
        newErrors.email = "Please enter a valid email address";
        isValid = false;
      }

      if (!form.phone.trim()) {
        newErrors.phone = "Phone number is required";
        isValid = false;
      }

      if (!form.password) {
        newErrors.password = "Password is required";
        isValid = false;
      } else if (form.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
        isValid = false;
      }

      if (!form.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
        isValid = false;
      } else if (form.password !== form.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  /* ========================= LOGIN ========================= */
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.login({
        email: form.email,
        password: form.password,
      });

      if (!data?.access_token) {
        throw new Error("Login response missing access token");
      }

      // ✅ Login with token and user data
      await setAuth(data.access_token, data.user);

      if (api?.setAuthToken) {
        api.setAuthToken(data.access_token);
      }

      const approvalStatus = data.user?.is_approved;

      // ✅ First time login → profile page
      if (approvalStatus === "00") {
        navigate("/profile", { replace: true });
      }
      // ✅ Pending approval → show modal
      else if (approvalStatus === "10") {
        setShowPendingModal(true);
      }
      // ✅ Approved → dashboard
      else if (approvalStatus === "01") {
        navigate("/dashboard", { replace: true });
      }

    } catch (error) {
      showToast(
        error.response?.data?.detail || error.message || "Login failed",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ======================== REGISTER ======================= */
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await api.register({
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        is_approved: "00",
      });
      showToast("Registration successful! Please verify your email.", "success");
      setMode("login");
      // Clear form on successful registration
      setForm({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
      });
      setErrors({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
      });
    } catch (error) {
      showToast(error.response?.data?.detail || "Registration failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // Clear errors when switching modes
  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setErrors({
      name: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    });
  };

  const slidePanel = {
    login: { x: "0%" },
    register: { x: "-66.6667%" },
  };

  const slideForm = {
    login: { x: "0%", opacity: 1 },
    register: { x: "150%", opacity: 1 },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F7FB] px-4 sm:px-6 py-8">
      <motion.div
        className="
         w-full 
          max-w-[1400px]
          h-auto
          sm:h-[600px]
          bg-white 
          rounded-3xl 
          shadow-2xl 
          flex 
          overflow-hidden 
          relative
        "
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* ================== BRAND PANEL (60%) ================== */}
        <motion.div
          variants={slidePanel}
          animate={mode}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          className="
           w-full
            sm:w-[60%]
            h-[250px]
            sm:h-full 
            absolute 
            right-0
            top-0
            sm:top-auto
            z-20
            flex flex-col items-center justify-center 
            text-white 
            p-8 sm:p-16 
            rounded-t-3xl sm:rounded-l-3xl sm:rounded-tr-none
            bg-gradient-to-br from-[#0A2A6A] to-[#153E75]
          "
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url(${duLogo})`,
              backgroundRepeat: "repeat",
              backgroundSize: "220px",
              mixBlendMode: "overlay",
            }}
          />

          <h1 className="relative text-4xl font-bold mb-4">
            {isLogin ? "Hello, Partner!" : "Welcome!"}
          </h1>

          <p className="relative text-white/90 text-center text-lg max-w-sm leading-relaxed">
            {isLogin
              ? "Login to access DU Global's enterprise-grade visa tools."
              : "Register with DU Global to access powerful B2B visa tools."}
          </p>
        </motion.div>

        {/* ================== WHITE FORM (40%) =================== */}
        <motion.div
          variants={slideForm}
          animate={mode}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          className="
            w-full
            sm:w-[40%]
            h-auto
            sm:h-full 
            p-6 sm:p-10
            pt-[270px] sm:pt-10
            bg-white 
            z-10
            flex flex-col justify-center
          "
        >
          <img src={duLogo} alt="DU Global" className="h-16 mx-auto mb-3" />

          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h2>

          {/* ======================= LOGIN ======================= */}
          {isLogin && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="w-full max-w-md mx-auto">
                <Label>Email Address <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => {
                    setForm({ ...form, email: e.target.value });
                    setErrors({ ...errors, email: "" });
                  }}
                  placeholder="agent@company.com"
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div className="w-full max-w-md mx-auto">
                <Label>Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    required
                    value={form.password}
                    onChange={(e) => {
                      setForm({ ...form, password: e.target.value });
                      setErrors({ ...errors, password: "" });
                    }}
                    placeholder="Enter password"
                    className={errors.password ? "border-red-500 pr-10" : "pr-10"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <div className="w-full max-w-md mx-auto">
                <Button
                  className="w-full bg-[#0A2A6A] hover:bg-[#2563EB] text-white"
                  disabled={loading}
                  type="submit"
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>
              </div>

              <p className="text-center text-sm mt-3">
                New Partner?{" "}
                <span
                  className="text-[#2563EB] cursor-pointer font-semibold"
                  onClick={() => handleModeSwitch("register")}
                >
                  Create Account
                </span>
              </p>
            </form>
          )}

          {/* ===================== REGISTER ====================== */}
          {!isLogin && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="w-full max-w-md mx-auto">
                <Label>Full Name <span className="text-red-500">*</span></Label>
                <Input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                    setErrors({ ...errors, name: "" });
                  }}
                  placeholder="John Doe"
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div className="w-full max-w-md mx-auto">
                <Label>Email Address <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => {
                    setForm({ ...form, email: e.target.value });
                    setErrors({ ...errors, email: "" });
                  }}
                  placeholder="agent@company.com"
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div className="w-full max-w-md mx-auto">
                <Label>Phone Number <span className="text-red-500">*</span></Label>
                <Input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => {
                    setForm({ ...form, phone: e.target.value });
                    setErrors({ ...errors, phone: "" });
                  }}
                  placeholder="1234567890"
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              <div className="w-full max-w-md mx-auto">
                <Label>Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    required
                    value={form.password}
                    onChange={(e) => {
                      setForm({ ...form, password: e.target.value });
                      setErrors({ ...errors, password: "" });
                    }}
                    placeholder="Minimum 8 characters"
                    className={errors.password ? "border-red-500 pr-10" : "pr-10"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <div className="w-full max-w-md mx-auto">
                <Label>Confirm Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={form.confirmPassword}
                    onChange={(e) => {
                      setForm({ ...form, confirmPassword: e.target.value });
                      setErrors({ ...errors, confirmPassword: "" });
                    }}
                    placeholder="Re-enter password"
                    className={errors.confirmPassword ? "border-red-500 pr-10" : "pr-10"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="w-full max-w-md mx-auto">
                <Button
                  className="w-full bg-[#0A2A6A] hover:bg-[#2563EB] text-white"
                  disabled={loading}
                  type="submit"
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </Button>
              </div>

              <p className="text-center text-sm mt-3">
                Already have an account?{" "}
                <span
                  className="text-[#2563EB] cursor-pointer font-semibold"
                  onClick={() => handleModeSwitch("login")}
                >
                  Login
                </span>
              </p>
            </form>
          )}
        </motion.div>
      </motion.div>

      <Modal
        open={showPendingModal}
        title="Account Pending Approval"
        message="Once your profile gets verified, you will be able to log in."
        onClose={() => {
          setShowPendingModal(false);
          logout();
        }}
      />
    </div>
  );
}